import type { TFunction } from 'i18next';

type BadgeVariant = 'default' | 'success' | 'warning' | 'error' | 'info';

type StatusLike = {
  statusCode?: string | null;
  statusDetail?: string | null;
};

/** Prefer workflow step detail when present (e.g. Pending Level 2 — HR Manager). */
export function hrRecordStatusLabel(record: StatusLike | string | null | undefined): string {
  if (record == null) {
    return '—';
  }
  if (typeof record === 'string') {
    return record;
  }
  return record.statusDetail?.trim() || record.statusCode?.trim() || '—';
}

const WORKFLOW_STATUS_KEYS: Record<string, `status.${string}`> = {
  PENDING: 'status.pending',
  APPROVED: 'status.approved',
  REJECTED: 'status.rejected',
  RETURNED: 'status.returned',
  CANCELLED: 'status.cancelled',
  COMPLETED: 'status.completed',
  DRAFT: 'status.draft',
  SUBMITTED: 'status.submitted',
  ESCALATED: 'status.escalated',
};

/** Localized workflow status for table badges (falls back to raw code). */
export function hrWorkflowStatusLabel(t: TFunction, statusCode?: string | null): string {
  const code = (statusCode ?? '').trim().toUpperCase();
  if (!code) return '—';
  const key = WORKFLOW_STATUS_KEYS[code];
  return key ? t(key, { ns: 'workflow' }) : code;
}

/** Maps HR record status strings to badge variants for consistent UI. */
export function hrStatusBadgeVariant(status: string): BadgeVariant {
  const s = status.toUpperCase();
  if (['APPROVED', 'ACTIVE', 'PRESENT', 'CONFIRMED', 'COMPLETED', 'PAID', 'PROCESSED'].some((x) => s.includes(x))) {
    return 'success';
  }
  if (['PENDING', 'DRAFT', 'SUBMITTED', 'PROBATION', 'IN_PROGRESS', 'OPEN', 'RETURNED'].some((x) => s.includes(x))) {
    return 'warning';
  }
  if (['REJECTED', 'CANCELLED', 'ABSENT', 'TERMINATED', 'FAILED'].some((x) => s.includes(x))) {
    return 'error';
  }
  if (['ON_LEAVE', 'LEAVE', 'INFO'].some((x) => s.includes(x))) {
    return 'info';
  }
  return 'default';
}

export function hrLifecycleEventOptions(t: TFunction) {
  return [
    { value: 'JOINING', label: t('lifecycleEvents.joining', { defaultValue: 'Joining' }) },
    { value: 'REJOINING', label: t('lifecycleEvents.rejoining', { defaultValue: 'Rejoining' }) },
    { value: 'CONFIRMED', label: t('lifecycleEvents.confirmed') },
    { value: 'PROMOTED', label: t('lifecycleEvents.promotion') },
    { value: 'TRANSFERRED', label: t('lifecycleEvents.transfer') },
    { value: 'PROBATION', label: t('lifecycleEvents.probation') },
    { value: 'TERMINATED', label: t('lifecycleEvents.termination') },
    { value: 'RESIGNED', label: t('lifecycleEvents.resignation') },
  ] as const;
}

/** @deprecated Use hrLifecycleEventOptions(t) */
export const HR_LIFECYCLE_EVENT_OPTIONS = [
  { value: 'CONFIRMED', label: 'Confirmed' },
  { value: 'PROMOTED', label: 'Promotion' },
  { value: 'TRANSFERRED', label: 'Transfer' },
  { value: 'PROBATION', label: 'Probation' },
  { value: 'TERMINATED', label: 'Termination' },
  { value: 'RESIGNED', label: 'Resignation' },
] as const;
